<template>
	<div class="chat-container">
		<button type="button" class="chat-back-btn" aria-label="返回首页" @click="goHome">
			<svg class="chat-back-btn__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
				<path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>
		</button>

		<!-- 左侧历史记录 -->
		<HistorySidebar
			ref="historySidebar"
			:current-conversation-id="currentConversationId"
			@select-chat="selectChat"
			@new-topic="handleNewTopic"
		/>

		<!-- 中间聊天区域 -->
		<div class="chat-main">
			<!-- 聊天消息区域（可滚动） -->
			<div class="chat-messages" ref="messagesContainer">
				<div v-for="(message, index) in messages" :key="message.id || `msg-${index}-${message.role}-${message.content?.substring(0, 20)}`" class="message-wrapper" :class="message.role">
					<div class="message-avatar">
						<img v-if="message.role === 'assistant'" src="@/assets/images/default_avatar.jpg" alt="ai" />
						<div v-else class="user-avatar">
							<i class="iconfont icon-avatar-fill"></i>
						</div>
					</div>
					<div class="message-content">
						<div class="message-bubble">
							<!-- 如果两者都有值，同时显示（思考过程在上，正式内容在下） -->
							<template v-if="message.content && message.reasoning_content">
								<div class="reasoning-content" v-html="formatMessage(message.reasoning_content)"></div>
								<div class="message-content-text" v-html="formatMessage(message.content)"></div>
							</template>
							<!-- 如果只有 reasoning_content（content 为空），显示思考内容（灰色） -->
							<div v-else-if="!message.content && message.reasoning_content" class="reasoning-content" v-html="formatMessage(message.reasoning_content)"></div>
							<!-- 如果只有 content，显示正式内容 -->
							<div v-else-if="message.content" class="message-content-text" v-html="formatMessage(message.content)"></div>
						</div>
					</div>
				</div>
			</div>

			<!-- 底部输入框和日期控件（固定） -->
			<div class="chat-input-container">
				<div class="textarea-wrapper">
					<textarea
						v-model="inputText"
						class="chat-input"
						:disabled="isSending || !currentConversationFinished"
						placeholder="请输入你想了解的问题"
						rows="4"
						@keydown.enter="handleKeyDown"
					>
					</textarea>
					<button class="send-btn" :disabled="isSending || !currentConversationFinished" @click="handleSend">
						<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
							<path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
					</button>
				</div>
				<!-- 日期展示和进度条 -->
				<div class="date-progress-wrapper">
					<div class="date-display" v-if="dateRangeText">
						{{ dateRangeText }}
					</div>
					<CircularProgress :percentage="progressPercentage" />
					
					<!-- 选项展示 -->
					<div class="options-display">
						<span class="option-display-item active">运营数据</span>
						<span 
							v-if="include_intent_detail" 
							class="option-display-item active"
						>
							客户需求挖掘与引导
						</span>
						<span 
							v-if="include_satisfaction_detail" 
							class="option-display-item active"
						>
							客户满意度
						</span>
						<span 
							v-if="include_quality_detail" 
							class="option-display-item active"
						>
							客户回复质量
						</span>
					</div>
					
					<!-- 百分比展示 -->
					<div class="percentage-display" v-if="detailSampleRate">
						{{ Math.round(detailSampleRate * 100) }}%
					</div>
				</div>
			</div>
		</div>

		<!-- 右侧推荐问题列表 -->
		<div class="chat-sidebar-right">
			<div class="suggestions-list">
				<span
					v-for="(suggestion, index) in relatedQuestions"
					:key="index"
					class="suggestion-item"
					@click="handleSuggestionClick(suggestion)"
				>
					{{ suggestion }}
				</span>
			</div>
		</div>
	</div>
</template>

<script>
import HistorySidebar from '@/components/HistorySidebar.vue';
import CircularProgress from '@/components/CircularProgress.vue';
import { marked } from 'marked';
import socketManager from '@/utils/socket';

// 配置 marked 选项（只需要配置一次）
marked.setOptions({
	breaks: true, // 支持换行
	gfm: true, // 支持 GitHub Flavored Markdown
	silent: true // 静默错误
});

export default {
	name: 'Chat',
	components: {
		HistorySidebar,
		CircularProgress
	},
		data() {
		return {
			currentConversationId: null,
			currentConversationTitle: '',
			currentConversationFinished: false,
			inputText: '',
			hasRefreshedHistory: false, // 标记是否已刷新历史记录
			messages: [],
			isSending: false, // 是否正在发送消息
			relatedQuestions: [
				'目前私域整体客户关系如何?',
				'改善当前客服质量最关键的动作是什么?',
				'上周损失了多少个引导客户成交的机会?',
				'除了客服个人能力,我们的内部流程有没有给客户带来麻烦?有什么证据?',
				'客服专业度如何?',
				'客服态度如何?',
				'客服积极性如何?'
			],
			dateRangeText: '',
			progressPercentage: 0,
			include_intent_detail: false,
			include_satisfaction_detail: false,
			include_quality_detail: false,
			detailSampleRate: 0.05 // 存储为小数（0.05 表示 5%）
		};
	},
	mounted() {
		this.initSocket();
		
		// 接收日期范围和进度
		const dateRangeParam = this.$route.query.dateRange;
		if (dateRangeParam) {
			const dates = dateRangeParam.split(',');
			if (dates.length === 2) {
				this.dateRangeText = `${dates[0]} 至 ${dates[1]}`;
			}
		}
		
		const progressParam = this.$route.query.progress;
		if (progressParam !== undefined) {
			this.progressPercentage = parseFloat(progressParam) || 0;
		}
		
		// 接收选项值
		if (this.$route.query.include_intent_detail !== undefined) {
			this.include_intent_detail = this.$route.query.include_intent_detail === 'true';
		}
		if (this.$route.query.include_satisfaction_detail !== undefined) {
			this.include_satisfaction_detail = this.$route.query.include_satisfaction_detail === 'true';
		}
		if (this.$route.query.include_quality_detail !== undefined) {
			this.include_quality_detail = this.$route.query.include_quality_detail === 'true';
		}
		
		// 接收百分比（转换为小数）
		if (this.$route.query.detail_sample_rate !== undefined) {
			const rate = parseFloat(this.$route.query.detail_sample_rate);
			// 如果接收到的值大于1，说明是百分比格式（如50），需要除以100
			// 如果小于等于1，说明已经是小数格式（如0.5）
			this.detailSampleRate = rate > 1 ? rate / 100 : rate;
		}
		
		// 如果从 URL 参数中传入了 conversationId，加载该话题的消息
		const conversationId = this.$route.query.conversationId;
		if (conversationId) {
			this.currentConversationId = String(conversationId);
			// 如果没有 progress 参数，尝试从历史记录中获取
			if (this.progressPercentage === 0 && this.$refs.historySidebar) {
				// 等待历史记录加载完成后，从历史记录中查找该 conversation 的详细信息
				this.$nextTick(async () => {
					await this.loadConversationInfoFromHistory(conversationId);
				});
			}
			this.fetchChatMessages(conversationId);
			// 如果 URL 中有 conversationId，说明是刷新页面或从历史记录进入，不需要刷新历史记录列表
			this.hasRefreshedHistory = true;
		} else {
			// 如果没有 conversationId，等待 DOM 更新后滚动到底部
			this.$nextTick(() => {
				setTimeout(() => {
					this.scrollToBottom();
				}, 100);
			});
		}
		
		// 如果有传入的初始消息，创建新聊天
		const initialMessage = this.$route.query.message;
		if (initialMessage && !conversationId) {
			this.createNewChat(initialMessage);
		}
	},
	beforeDestroy() {
		this.destroySocket();
	},
	methods: {
		goHome() {
			this.$router.push({ name: 'Home' });
		},
		// 选择聊天
		async selectChat(chat) {
			this.currentConversationId = String(chat.id);
			this.currentConversationTitle = chat.title;
			this.currentConversationFinished = chat.status === 'success';
			
			// 更新日期范围和进度
			if (chat.data_start_date && chat.data_end_date) {
				this.dateRangeText = `${chat.data_start_date} 至 ${chat.data_end_date}`;
			}
			if (chat.token_length && chat.token_limit) {
				this.progressPercentage = Math.min(
					parseFloat(((chat.token_length / chat.token_limit) * 100).toFixed(2)),
					100
				);
			}
			
			// 更新选项值
			if (chat.include_intent_detail !== undefined) {
				this.include_intent_detail = chat.include_intent_detail;
			}
			if (chat.include_satisfaction_detail !== undefined) {
				this.include_satisfaction_detail = chat.include_satisfaction_detail;
			}
			if (chat.include_quality_detail !== undefined) {
				this.include_quality_detail = chat.include_quality_detail;
			}
			
			// 更新百分比（detail_sample_rate）
			if (chat.detail_sample_rate !== undefined) {
				const rate = parseFloat(chat.detail_sample_rate);
				// 如果值大于1，说明是百分比格式（如50），需要除以100
				// 如果小于等于1，说明已经是小数格式（如0.5）
				this.detailSampleRate = rate > 1 ? rate / 100 : rate;
			}
			
			// 更新 URL 参数，包括 progress，这样刷新后不会丢失
			this.$router.replace({
				path: this.$route.path,
				query: {
					...this.$route.query,
					conversationId: String(chat.id),
					progress: this.progressPercentage,
					dateRange: chat.data_start_date && chat.data_end_date 
						? `${chat.data_start_date},${chat.data_end_date}` 
						: this.$route.query.dateRange,
					include_intent_detail: this.include_intent_detail ? 'true' : 'false',
					include_satisfaction_detail: this.include_satisfaction_detail ? 'true' : 'false',
					include_quality_detail: this.include_quality_detail ? 'true' : 'false',
					detail_sample_rate: this.detailSampleRate
				}
			});
			
			// 获取历史对话消息
			await this.fetchChatMessages(chat.id);
			
			this.$nextTick(() => {
				this.scrollToBottom();
			});
		},
		// 获取聊天消息
		async fetchChatMessages(conversationId) {
			if (!conversationId) {
				this.messages = [];
				return Promise.resolve();
			}
			
			try {
				const res = await this.$get(`/manager-conversation/${conversationId}/messages`);
				if (res.code === 200 && res.data) {
					
					this.messages = Array.isArray(res.data) ? res.data.map(msg => ({
						id: msg.id,
						role: msg.role,
						content: msg.content || msg.message || '',
						reasoning_content: msg.reasoning_content || '',
						timestamp: msg.created_at || msg.timestamp,
						finished: msg.finished !== undefined ? msg.finished : true
					})) : [];
					
					// 根据最后一条消息的 finished 状态设置 currentConversationFinished
					if (this.messages.length > 0) {
						const lastMessage = this.messages[this.messages.length - 1];
						this.currentConversationFinished = lastMessage.finished !== undefined ? lastMessage.finished : true;
					} else {
						this.currentConversationFinished = true;
					}
					
					this.$nextTick(() => {
						this.scrollToBottom();
					});
				} else {
					this.messages = [];
					this.currentConversationFinished = true;
				}
				return Promise.resolve();
			} catch (error) {
				this.messages = [];
				this.$message.error('获取聊天消息失败');
				return Promise.reject(error);
			}
		},
		// 创建新聊天
		createNewChat(message) {
			// 重置对话状态
			this.currentConversationId = null;
			this.currentConversationTitle = '';
			this.currentConversationFinished = false;
			this.messages = [];
			// 发送初始消息
			this.sendMessage(message);
		},
		// 开启新话题
		handleNewTopic() {
			this.$router.push('/');
		},
		// 处理键盘事件
		handleKeyDown(event) {
			// Shift+Enter 或 Ctrl+Enter 或 Cmd+Enter：允许换行（不阻止默认行为）
			if (event.shiftKey || event.ctrlKey || event.metaKey) {
				return; // 允许默认行为（换行）
			}
			
			// Enter（无修饰键）：发送消息
			event.preventDefault(); // 阻止默认换行行为
			this.handleSend();
		},
		// 发送消息
		handleSend() {
			if (!this.inputText.trim()) {
				return;
			}
			// 先检查是否可以发送
			if (!this.currentConversationFinished || this.isSending) {
				if (!this.currentConversationFinished) {
					this.$message.warning('AI 正在回复中，请稍候...');
				}
				return;
			}
			
			const message = this.inputText.trim();
			// 立即清空输入框，提升响应速度
			this.inputText = '';
			
			// 立即添加消息到界面，确保用户看到即时反馈
			this.messages.push({
				role: 'user',
				content: message,
				isSending: true
			});
			
			// 立即滚动到底部
			this.$nextTick(() => {
				this.scrollToBottom();
			});
			
			// 异步发送消息
			this.sendMessage(message);
		},
		// 发送消息（实际处理）
		async sendMessage(message) {
			// 设置发送状态
			this.isSending = true;

			try {
				await this.sendMessageViaAPI(message);
			} catch (error) {
				// 如果发送失败，重置发送状态，允许用户重试
				// 注意：正常情况下 isSending 会在收到 AI 回复后重置
				this.isSending = false;
				this.currentConversationFinished = true;
			}
		},
		// 初始化 Socket 连接
		initSocket() {
			socketManager.connect({
				onConnect: (socket) => {
					// this.$message.success('连接成功');
					
					// 加入房间
					const userId = this.$store.state.userInfo?.userId || 
					                localStorage.getItem('userId') ||
					                this.$store.state.userInfo?.user_id;
					
					if (userId) {
						socket.emit('join_manager_admin_room', {user_id: userId});
					}
				},
				onError: (error) => {
					console.error('Socket 连接错误:', error);
					this.$message.error('连接失败，请检查网络');
				},
				onDisconnect: (reason) => {
					if (reason === 'io server disconnect') {
						// 服务器主动断开，需要手动重连
						socketManager.connect();
					}
				},
				onReconnect: (attemptNumber) => {
					this.$message.success('重新连接成功');
					
					const socket = socketManager.getSocket();
					
					const userId = this.$store.state.userInfo?.userId || 
					                localStorage.getItem('userId') ||
					                this.$store.state.userInfo?.user_id;
									
					if (socket && userId) {
						socket.emit('join_manager_admin_room', {user_id: userId});
					}
				},
				onReconnectFailed: () => {
					this.$message.error('连接失败，请刷新页面重试');
				}
			});

			// 监听 AI 回复消息
			socketManager.on('manager_chat_completion', (data) => {
				this.handleAIResponse(data);
			});

			// 监听错误消息
			socketManager.on('error', (error) => {
				this.$message.error(error.message || '发生错误');
			});
		},
		
		destroySocket() {
			// 移除所有监听器
			socketManager.off('manager_chat_completion');
			socketManager.off('error');
			
			// 断开连接
			socketManager.disconnect();
		},
		// 通过 API 发送消息
		async sendMessageViaAPI(message) {
			// 从 dateRangeText 中提取日期范围
			let startDate = null;
			let endDate = null;
			
			if (this.dateRangeText) {
				const dateMatch = this.dateRangeText.match(/(\d{4}-\d{2}-\d{2})\s*至\s*(\d{4}-\d{2}-\d{2})/);
				if (dateMatch) {
					startDate = dateMatch[1];
					endDate = dateMatch[2];
				}
			}
			
			if (!startDate || !endDate) {
				this.$message.warning('请先选择日期范围');
				this.isSending = false; // 重置发送状态
				return;
			}

			try {
				// 确保传递正确的 conversation_id（如果是新对话则为 null）
				const conversationId = this.currentConversationId || null;
				
				const res = await this.$post('/manager-conversation/chat-completion', {
					content: message,
					conversation_id: conversationId,
					start_date: startDate,
					end_date: endDate,
					include_intent_detail: this.include_intent_detail,
					include_satisfaction_detail: this.include_satisfaction_detail,
					include_quality_detail: this.include_quality_detail,
					detail_sample_rate: this.detailSampleRate
				});

				if (res.code === 200) {
					// 如果返回了新的 conversation_id 或 id，更新当前对话 ID
					const conversationId = res.data?.id
					if (conversationId) {
						const newConversationId = String(conversationId);
						const isNewConversation = !this.currentConversationId || this.currentConversationId !== newConversationId;
						
						// 保存 conversation_id，确保后续消息使用同一个对话
						this.currentConversationId = newConversationId;
						
						// 更新 URL 中的 conversationId，这样刷新页面时不会丢失
						if (this.$route.query.conversationId !== newConversationId) {
							this.$router.replace({
								path: this.$route.path,
								query: {
									...this.$route.query,
									conversationId: newConversationId
								}
							});
						}
						
						// 如果是新对话，刷新左侧历史记录列表并选中当前话题
						if (isNewConversation && !this.hasRefreshedHistory) {
							this.hasRefreshedHistory = true;
							// 等待一下确保 currentConversationId 已更新
							this.$nextTick(() => {
								if (this.$refs.historySidebar) {
									this.$refs.historySidebar.refreshHistory();
									// 刷新完成后重置标记（延迟重置，避免短时间内重复刷新）
									setTimeout(() => {
										this.hasRefreshedHistory = false;
									}, 1000);
								}
							});
						}
						
						// 如果是新对话，更新标题
						if (res.data.title) {
							this.currentConversationTitle = res.data.title;
						}
						if (res.data.token_length && res.data.token_limit) {
							this.progressPercentage = Math.min(
								parseFloat(((res.data.token_length / res.data.token_limit) * 100).toFixed(2)),
								100
							);
							// 更新 URL 中的 progress 参数，这样刷新后不会丢失
							this.$router.replace({
								path: this.$route.path,
								query: {
									...this.$route.query,
									progress: this.progressPercentage
								}
							});
						}
					}
					
				} else {
					this.$message.error(res.msg || '发送消息失败');
				}
			} catch (error) {
				console.error('发送消息失败:', error);
				this.$message.error('发送消息失败，请稍后重试');
				this.isSending = false; // 重置发送状态
			}
		},
		// 处理 AI 回复（事件监听方式）
		handleAIResponse(data) {
			// 如果 currentConversationId 为空，但收到的消息有 conversation_id，则更新它
			// 这可以处理 Socket 消息在 API 返回之前到达的情况
			if (!this.currentConversationId && data.conversation_id) {
				const newConversationId = String(data.conversation_id);
				this.currentConversationId = newConversationId;
				
				// 更新 URL 中的 conversationId，这样刷新页面时不会丢失
				if (this.$route.query.conversationId !== newConversationId) {
					this.$router.replace({
						path: this.$route.path,
						query: {
							...this.$route.query,
							conversationId: newConversationId
						}
					});
				}
				
				// 如果是新话题，刷新左侧历史记录列表（避免与 API 返回时的刷新重复）
				// 但只有在 URL 中没有 conversationId 时才刷新（避免刷新页面时重复刷新）
				if (!this.hasRefreshedHistory && !this.$route.query.conversationId) {
					this.hasRefreshedHistory = true;
					this.$nextTick(() => {
						if (this.$refs.historySidebar) {
							this.$refs.historySidebar.refreshHistory();
							// 刷新完成后重置标记（延迟重置，避免短时间内重复刷新）
							setTimeout(() => {
								this.hasRefreshedHistory = false;
							}, 1000);
						}
					});
				}
			}
			
			// 移除加载中的消息
			const loadingIndex = this.messages.findIndex(msg => msg.id === data.id);
			if (loadingIndex !== -1) {
				this.messages.splice(loadingIndex, 1);
			}
			
			// 确保 conversation_id 匹配（转换为字符串比较）
			// 如果 currentConversationId 是 null，且收到的消息也没有 conversation_id，则允许通过（兼容旧逻辑）
			if (this.currentConversationId && String(data.conversation_id) !== String(this.currentConversationId)) {
				return;
			}
			
			// 如果是用户消息，检查是否已存在（避免重复添加）
			if (data.role === 'user') {
				// 通过 id 或 content 匹配已存在的用户消息
				const existingUserMsg = this.messages.find(msg => 
					msg.role === 'user' && 
					(msg.id === data.id || msg.content === data.content)
				);
				if (existingUserMsg) {
					// 如果已存在，只更新 id 等字段，不重复添加
					if (data.id && !existingUserMsg.id) {
						existingUserMsg.id = data.id;
					}
					return;
				}
			}
			
			const msgIndex = this.messages.findIndex(msg => msg.id === data.id);
			const { finished, ...msgFields } = data;
			
			// 确保消息对象包含必要的字段
			const messageData = {
				id: data.id,
				role: data.role,
				content: data.content || '',
				reasoning_content: data.reasoning_content || '',
				conversation_id: data.conversation_id,
				...msgFields
			};
			
			if (msgIndex !== -1) {
				// 更新现有消息
				this.messages[msgIndex] = {
					...messageData
				};
			} else {
				// 添加新消息
				this.messages.push(messageData);
			}
			
			this.currentConversationFinished = data.finished;
			
			// 如果消息已完成，重置发送状态
			if (data.finished) {
				this.isSending = false;
			}

			this.$nextTick(() => {
				this.scrollToBottom();
			});
		},
		// 点击推荐问题
		handleSuggestionClick(suggestion) {
			this.sendMessage(suggestion);
		},
		// 滚动到底部
		scrollToBottom() {
			if (this.$refs.messagesContainer) {
				this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
			}
		},
		// 格式化消息内容（支持 markdown）
		formatMessage(content) {
			if (!content) return '';
			// 使用 marked 解析 markdown
			try {
				// 配置 marked 选项
				marked.setOptions({
					breaks: true, // 支持换行
					gfm: true, // 支持 GitHub Flavored Markdown
					sanitize: false, // 允许 HTML（因为使用 v-html）
					silent: true // 静默错误
				});
				return marked.parse(content);
			} catch (error) {
				console.error('Markdown 解析错误:', error);
				// 如果解析失败，回退到简单的换行处理
				return content.replace(/\n/g, '<br>');
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@use '@/styles/markdown.scss' as *;

.chat-back-btn {
	position: fixed;
	left: 30px;
	top: 12px;
	z-index: 20;
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 6px 12px;
	border: none;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 1px 8px rgba(0, 0, 0, 0.12);
	font-size: 14px;
	color: #333;
	cursor: pointer;
	transition: color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}
.chat-back-btn__icon {
	flex-shrink: 0;
	color: currentColor;
}

.chat-container {
	display: flex;
	height: 100vh;
	background: #f5f5f5;
	padding: 50px 0 28px 340px;
}


.chat-main {
	flex: 1;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.chat-messages {
	flex: 1;
	overflow-y: auto;
	padding: 20px;
	display: flex;
	flex-direction: column;
	gap: 20px;
}

.message-wrapper {
	display: flex;
	gap: 12px;
}

.message-wrapper.user {
	flex-direction: row-reverse;
}

.message-avatar {
	width: 32px;
	height: 32px;
	flex-shrink: 0;
}

.message-avatar img {
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
}

.user-avatar {
	width: 100%;
	height: 100%;
	border-radius: 50%;
	background: #fdc408;
	display: flex;
	align-items: center;
	justify-content: center;

	.icon-avatar-fill {
		color: #fff;
		font-size: 24px;
	}
}

.message-content {
	flex: 1;
	display: flex;
}

.message-wrapper.user .message-content {
	justify-content: flex-end;

	.message-bubble {
		max-width: 70%;
	}
}

.message-wrapper.ai .message-content {
	justify-content: flex-start;
}

.message-bubble {
	display: inline-block;
	padding: 10px 20px;
	border-radius: 8px;
	background: #fff;
	line-height: 1.8;
	word-wrap: break-word;
	max-width: 100%;
}

.reasoning-content {
	color: #999;
	font-style: italic;
	margin-bottom: 8px;
	padding-bottom: 8px;
}

.message-content-text {
	color: #333;
}




.message-wrapper.user .message-bubble {
	background: #fff1be;
}


/* 底部输入框 */
.chat-input-container {
	box-sizing: border-box;
	width: 100%;
	padding: 16px 20px;
	position: relative;
}

.date-progress-wrapper {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 16px;
	margin-top: 12px;
	justify-content: flex-start;
	flex-wrap: wrap;
}

.date-display {
	font-size: 12px;
	color: #666;
	padding: 6px 12px;
	background: #ebebec;
	border-radius: 4px;
}

.options-display {
	display: flex;
	gap: 8px;
	align-items: center;
	flex-wrap: wrap;
}

.option-display-item {
	padding: 6px 12px;
	background: #fdc408;
	border-radius: 4px;
	font-size: 12px;
	color: #333;
	font-weight: 500;
	white-space: nowrap;
	
	&.active {
		background: #fdc408;
	}
}

.percentage-display {
	font-size: 12px;
	color: #666;
	padding: 6px 12px;
	background: #ebebec;
	border-radius: 4px;
	font-weight: 500;
}

.textarea-wrapper {
	position: relative;
	width: 100%;
}

.chat-input {
	width: 100%;
	padding: 12px 50px 12px 16px;
	box-sizing: border-box;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	background: #fff;
	outline: none;
	transition: all 0.3s ease;
	box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.2);
	resize: none;
	line-height: 1.5;
	font-size: 14px;
	font-family: inherit;
}

.chat-input:focus {
	border-color: #fdc408;
	box-shadow: 0 2px 12px rgba(253, 196, 8, 0.15);
}

.chat-input:disabled {
	background: #f5f5f5;
	cursor: not-allowed;
	opacity: 0.6;
}

.send-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.textarea-wrapper .send-btn {
	position: absolute;
	right: 8px;
	bottom: 8px;
	width: 28px;
	height: 28px;
	border: none;
	background: #ffd700;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: #fff;
	z-index: 10;
	transition: all 0.3s ease;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chat-input-container .send-btn:hover {
	background: #ffed4e;
	transform: scale(1.05);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.date-progress-wrapper {
	display: flex;
	align-items: center;
	gap: 16px;
}

.date-display {
	padding: 10px 16px;
	background: #ebebec;
	border-radius: 6px;
	font-size: 12px;
	color: #333;
	min-width: 220px;
	text-align: center;
}


.chat-sidebar-right {
	width: 280px;
	// border-left: 1px solid #e0e0e0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.suggestions-list {
	flex: 1;
	overflow-y: auto;
	padding: 16px 20px;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.suggestions-list .suggestion-item {
	display: inline-block;
	padding: 8px 16px;
	background: #e0e0e0;
	border-radius: 6px;
	cursor: pointer;
	word-break: break-word;
	width: fit-content;
	max-width: 100%;
}

</style>

